import { clsx } from 'clsx';
import { CSSProperties, FocusEvent, forwardRef, ReactElement, Ref, useMemo } from 'react';
import { Placement } from 'react-aria';
import {
  Select as AriaSelect,
  Autocomplete,
  Button,
  ButtonContext,
  FieldError,
  Key,
  Label,
  SelectValue,
  Text,
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import IconButton from '~/components/buttons/NewIconButton';
import Icon from '~/components/data-display/Icon';
import fieldStyles from '~/components/inputs/Field.module.scss';
import { ListBoxItems, ListBoxPopoverContainer } from '~/components/inputs/Select/ListBoxPopover';
import SearchField from '~/components/inputs/TextField/SearchField';
import { HStack } from '~/components/layout/Stack';
import useDataTestId from '~/hooks/useDataTestId';
import ChevronIcon from '~/icons/icon-chevron-down.svg?react';
import IconClose from '~/icons/icon-close.svg?react';
import { isDefined } from '~/types/typeGuards';
import styles from './Select.module.scss';

export interface SelectOption<T> {
  label: string;
  key: string;
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  media?: ReactElement;
  selectedLabel?: string;
  selectedDisplayLabel?: string;
  description?: string;
  action?: ReactElement;
  value: T;
  isDisabled?: boolean;
  className?: string;
}

export interface BaseSelectProps {
  className?: string;
  errorMessage?: string;
  helpText?: string;
  hideLabel?: boolean;
  id?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
  label: string;
  name?: string;
  onBlur?: (e: FocusEvent<Element, Element>) => void;
  placeholder?: string;
  size?: 'small' | 'medium' | 'large';
  style?: CSSProperties;
  dataTestId?: string;
  buttonClassName?: string;
  mediaClassName?: string;
  onFilterChange?: (value: string) => void;
  filterText?: string;
  filterPlaceholder?: string;
  filterLabel?: string;
}

interface Props<T> extends BaseSelectProps {
  options: SelectOption<T | null>[];
  emptyValue?: string;
  onChange: (value: T | null) => void;
  clearable?: boolean;
  value: T | null;
  listBoxPopoverPlacement?: Placement;
}

const Select = <T,>(
  { size = 'medium', options, clearable, emptyValue, ...props }: Props<T>,
  ref: Ref<HTMLDivElement>,
) => {
  const dt = useDataTestId(props.dataTestId);
  const { t } = useTranslation();

  const handleChange = (key: Key | null) => {
    if (key === null && clearable) {
      return props.onChange(null);
    }

    const newValue = options.find((x) => x.key === key);
    if (newValue?.value === emptyValue) {
      return props.onChange(null);
    }

    if (newValue) {
      return props.onChange(newValue.value);
    }
  };
  const selectedOption = options.find((o) => o.value === props.value);
  const selectedKey = selectedOption?.key ?? null;

  const listBoxOptions = useMemo(() => {
    if (options.length === 0) {
      return [
        {
          id: `${props.id}_empty-state`,
          label: t('NO_AVAILABLE_OPTIONS'),
          textValue: `${props.id}_empty-state`,
          isDisabled: true,
        },
      ];
    }

    return options.map((option) => ({
      description: option.description,
      id: option.key,
      icon: option.Icon,
      Icon: option.Icon,
      label: option.label,
      media: option.media,
      action: option.action,
      textValue: option.label,
      isDisabled: option.isDisabled,
      className: option.className,
    }));
  }, [options, t, props.id]);

  return (
    <AriaSelect
      ref={ref}
      aria-label={props.hideLabel ? props.label : undefined}
      className={clsx(fieldStyles.field, styles.selectField, props.className)}
      data-size={size}
      data-testid={dt()}
      id={props.id}
      isDisabled={props.isDisabled}
      isInvalid={props.isInvalid || !!props.errorMessage}
      isRequired={props.isRequired}
      name={props.name}
      onBlur={props.onBlur}
      onChange={handleChange}
      style={props.style}
      placeholder={props.placeholder}
      validationBehavior="aria"
      value={selectedKey}
    >
      {!props.hideLabel && (
        <Label>
          {props.label} {props.isRequired ? ' *' : null}
        </Label>
      )}

      <div className={styles.triggerContainer}>
        <Button className={clsx(props.buttonClassName, styles.valueContainer)} data-testid={dt('trigger')}>
          {selectedOption?.Icon && <selectedOption.Icon className={styles.icon} />}
          {selectedOption?.media && (
            <div className={clsx(styles.media, props.mediaClassName)}>{selectedOption.media}</div>
          )}
          {selectedOption?.selectedDisplayLabel && (
            <span className={styles.selectedDisplayLabel}>{selectedOption.selectedDisplayLabel}</span>
          )}
          <SelectValue className={styles.value}>
            {({ isPlaceholder }) => {
              return isPlaceholder ? props.placeholder : (selectedOption?.selectedLabel ?? selectedOption?.label);
            }}
          </SelectValue>
          <HStack>
            {clearable && isDefined(props.value) && <Icon component={IconClose} className={clsx(styles.fakeClear)} />}
            <Icon component={ChevronIcon} />
          </HStack>
        </Button>

        {clearable && isDefined(props.value) && (
          <ClearButton disabled={props.isDisabled} onPress={() => handleChange(null)} />
        )}
      </div>

      {props.helpText && <Text slot="description">{props.helpText}</Text>}

      <FieldError>{props.errorMessage}</FieldError>

      <ListBoxPopoverContainer placement={props.listBoxPopoverPlacement} offset={1}>
        {props.onFilterChange ? (
          <Autocomplete>
            <SearchField
              autofocus
              onChange={props.onFilterChange}
              label={props.filterLabel ?? ''}
              placeholder={props.filterPlaceholder}
              hideLabel
              className={styles.searchField}
              value={props.filterText ?? ''}
            />
            <ListBoxItems options={listBoxOptions} />
          </Autocomplete>
        ) : (
          <ListBoxItems options={listBoxOptions} />
        )}
      </ListBoxPopoverContainer>
    </AriaSelect>
  );
};

const ClearButton = ({ onPress, disabled = false }: { onPress: () => void; disabled?: boolean }) => {
  return (
    /**
     * ButtonContext.Provider isolates the clear button from react-aria's Select context
     * to prevent it from being attached to the select's button functionality
     */
    <ButtonContext.Provider value={null}>
      <IconButton
        className={styles.clear}
        iconClassName={styles.icon}
        Icon={IconClose}
        variant="ghost"
        size="small"
        disabled={disabled}
        onPressStart={onPress}
      />
    </ButtonContext.Provider>
  );
};

export default forwardRef(Select) as <T>(p: Props<T> & { ref?: Ref<HTMLDivElement> }) => ReactElement;
